/**
 * useEventSource - React hook for SSE real-time updates
 *
 * Connects to backend SSE endpoint and routes events to
 * React Query cache invalidation.
 *
 * Pattern: Follows useNotifications.ts React Query patterns.
 *
 * WP-RT-2: Frontend Real-Time Integration
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { createEventSource, SSEEvent, SSEEventType } from '@/services/events';
import { useAuth } from '@/hooks/useAuth';
import { useSyncStore } from '@/store/syncStore';

// Query keys to invalidate per event type
// MUST match SSEEventType and SSE_FORWARDED_EVENTS in WP-RT-1
const EVENT_INVALIDATIONS: Record<SSEEventType, string[][]> = {
  // Pseudo-event for connection state
  connected: [],

  // Sync lifecycle events
  'sync.started': [],
  'sync.completed': [['connections'], ['accounts'], ['transactions'], ['balances']],
  'sync.failed': [['connections']],
  'holdings.synced': [
    ['accounts'], // Investment accounts updated
    ['holdings'], // Holdings data refreshed
    ['allocations'], // Asset allocation recalculated
  ],

  // Account events
  'account.disconnected': [['connections'], ['accounts']],
  'balance.mismatch': [['accounts'], ['balances']],

  // Connection events
  'connection.deleted': [
    ['connections'],
    ['accounts'], // Accounts belong to connection
    ['transactions'], // Transactions belong to accounts
  ],

  // Notification events
  'notification.created': [['notifications']],
};

export interface UseEventSourceOptions {
  /**
   * Callback when an event is received (for custom handling)
   */
  onEvent?: (event: SSEEvent) => void;

  /**
   * Whether to disable polling when SSE is connected.
   * Default: true
   */
  disablePollingWhenConnected?: boolean;
}

export interface UseEventSourceResult {
  /**
   * Whether SSE is currently connected
   */
  isConnected: boolean;

  /**
   * Last error if connection failed
   */
  lastError: Event | null;
}

/**
 * Hook that manages SSE connection and routes events to React Query.
 *
 * Usage:
 * ```tsx
 * // In App.tsx or a top-level provider
 * function App() {
 *   useEventSource();  // Just mount it, handles everything
 *   return <RouterProvider ... />;
 * }
 * ```
 */
export function useEventSource(options: UseEventSourceOptions = {}): UseEventSourceResult {
  const { onEvent, disablePollingWhenConnected = true } = options;

  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const { setSyncing, clearAll: clearSyncState } = useSyncStore();

  const [isConnected, setIsConnected] = useState(false);
  const [lastError, setLastError] = useState<Event | null>(null);
  const connectionRef = useRef<ReturnType<typeof createEventSource> | null>(null);

  // Handle incoming events
  const handleEvent = useCallback(
    (event: SSEEvent) => {
      // Call custom handler if provided
      onEvent?.(event);

      // Handle sync state updates (not query invalidation)
      // sync.started -> mark connection as syncing
      // sync.completed/failed -> mark connection as not syncing
      const connectionId = event.payload?.connectionId as string | undefined;
      if (connectionId) {
        if (event.eventType === 'sync.started') {
          setSyncing(connectionId, true);
        } else if (event.eventType === 'sync.completed' || event.eventType === 'sync.failed') {
          setSyncing(connectionId, false);
        }
      }

      // Invalidate relevant queries
      const keysToInvalidate = EVENT_INVALIDATIONS[event.eventType] || [];
      keysToInvalidate.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });

      // Log for debugging
      if (import.meta.env.DEV) {
        console.log('[SSE] Event received:', event.eventType, event.payload);
      }
    },
    [queryClient, onEvent, setSyncing]
  );

  // Manage connection lifecycle
  useEffect(() => {
    if (!isAuthenticated) {
      // Close existing connection if user logs out
      connectionRef.current?.close();
      connectionRef.current = null;
      setIsConnected(false);
      clearSyncState(); // Clear sync state on logout
      return;
    }

    // Create connection
    connectionRef.current = createEventSource({
      onEvent: handleEvent,
      onConnect: () => {
        setIsConnected(true);
        setLastError(null);
      },
      onDisconnect: () => {
        setIsConnected(false);
      },
      onError: (error) => {
        setLastError(error);
      },
    });

    // Cleanup on unmount
    return () => {
      connectionRef.current?.close();
      connectionRef.current = null;
    };
  }, [isAuthenticated, handleEvent, clearSyncState]);

  // Adjust polling based on connection status
  useEffect(() => {
    if (!disablePollingWhenConnected) return;

    if (isConnected) {
      // Disable polling for notification count when SSE is connected
      // This is handled by setting refetchInterval to false in the hook
      // We store this in a ref that useUnreadCount can check
      (window as unknown as { __sseConnected?: boolean }).__sseConnected = true;
    } else {
      (window as unknown as { __sseConnected?: boolean }).__sseConnected = false;
    }
  }, [isConnected, disablePollingWhenConnected]);

  return {
    isConnected,
    lastError,
  };
}

export default useEventSource;
